import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, ShieldOff, Trash2 } from "lucide-react";

const REASON_STYLE = {
  bounced: "bg-crimson/12 text-crimson",
  unsubscribed: "bg-yellow-100 text-yellow-700",
  spam: "bg-crimson/12 text-crimson",
  manual: "bg-muted text-muted-foreground",
};

export default function Suppressions() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const load = async () => {
    const params = { ...(activeCompanyId ? { company_id: activeCompanyId } : {}), ...(q ? { q } : {}) };
    const { data } = await api.get("/suppressions", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId, q]);

  const add = async () => {
    if (!newEmail || !activeCompanyId) return toast.error("Select a workspace and enter an email");
    try {
      await api.post("/suppressions", { company_id: activeCompanyId, email: newEmail, reason: "manual" });
      toast.success(`Suppressed ${newEmail}`);
      setNewEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Unblock this recipient?")) return;
    try { await api.delete(`/suppressions/${id}`); toast.success("Unblocked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="suppressions-page">
      <div className="rounded-2xl border border-border bg-white p-3 flex items-center gap-2 flex-wrap">
        <ShieldOff className="h-4 w-4 text-crimson" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{items.length} suppressed</span>
        <Input placeholder="Search email…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-56 rounded-sm ml-2" data-testid="suppression-search" />
        <div className="ml-auto flex items-center gap-1">
          <Input placeholder="email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="h-8 w-64 rounded-sm text-xs" data-testid="suppression-new-email" />
          <Button size="sm" onClick={add} className="rounded-sm" data-testid="suppression-add"><Plus className="h-3.5 w-3.5 mr-1" /> Suppress</Button>
        </div>
      </div>
      <div className="border border-border rounded-sm overflow-hidden bg-white">
        <table className="tbl w-full" data-testid="suppressions-table">
          <thead><tr><th>Email</th><th>Reason</th><th>Source</th><th>Added</th><th></th></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`suppression-row-${s.id}`}>
                <td className="font-medium">{s.email}</td>
                <td><Badge className={`rounded-sm text-[10px] font-mono uppercase ${REASON_STYLE[s.reason] || REASON_STYLE.manual}`}>{s.reason}</Badge></td>
                <td className="font-mono text-[10px] text-muted-foreground">{s.source}</td>
                <td className="font-mono text-[10px] text-muted-foreground">{s.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-crimson" onClick={() => remove(s.id)} data-testid={`suppression-remove-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="text-center text-muted-foreground py-8">No suppressions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
